import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Key, Copy, Plus, Trash2, Power, PowerOff, Check, FileText, FileDown } from "lucide-react";
import { api, adminAuthHeaders } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function maskKey(k) {
  if (!k || k.length < 12) return k || "";
  return k.slice(0, 8) + "•••••••••••••••" + k.slice(-4);
}

export default function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/api-keys", { headers: adminAuthHeaders() });
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Nie udało się wczytać kluczy API");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) {
      toast.error('Podaj nazwę (np. "Partner XYZ")');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name: newName.trim() }, { headers: adminAuthHeaders() });
      toast.success("Klucz wygenerowany. Skopiuj go teraz!");
      setNewName("");
      setRevealed({ ...revealed, [data.id]: true }); // od razu odsłonięty świeżo utworzony
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd tworzenia klucza");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (k) => {
    try {
      await api.patch(`/admin/api-keys/${k.id}`, { active: !k.active }, { headers: adminAuthHeaders() });
      toast.success(k.active ? "Klucz zablokowany" : "Klucz aktywowany");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd");
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`Trwale usunąć klucz „${k.name}"? Ta operacja jest nieodwracalna.`)) return;
    try {
      await api.delete(`/admin/api-keys/${k.id}`, { headers: adminAuthHeaders() });
      toast.success("Klucz usunięty");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd usuwania");
    }
  };

  const copyKey = async (k) => {
    try {
      await navigator.clipboard.writeText(k.key);
      setCopied(k.id);
      setTimeout(() => setCopied(null), 2000);
      toast.success("Klucz skopiowany");
    } catch (err) {
      toast.error("Nie udało się skopiować (kliknij ręcznie i skopiuj)");
    }
  };

  const downloadDocs = async (k, format) => {
    try {
      const url = `${BACKEND_URL}/api/admin/api-keys/${k.id}/docs.${format}`;
      const token = localStorage.getItem("rm_admin_token");
      const response = await fetch(url, { headers: { "X-Admin-Token": token || "" } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const safeName = (k.name || "partner").replace(/\s+/g, "-");
      a.download = `rmobile-api-instrukcja-${safeName}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Pobrano ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(`Nie udało się pobrać ${format.toUpperCase()}: ${err.message}`);
    }
  };

  const publicUrl = `${BACKEND_URL}/api/public/stats`;

  return (
    <div data-testid="api-keys-panel" className="rm-card p-6 md:p-8 space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#1f3a8a] flex items-center gap-2">
          <Key size={22} /> Klucze API (dla firm zewnętrznych)
        </h2>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Każda firma zewnętrzna, która ma dostać statystyki (online/dzisiaj/łącznie), musi mieć <b>własny klucz API</b>.
          Bez klucza endpoint zwraca 401. Możesz w każdej chwili <b>zablokować</b> lub <b>usunąć</b> klucz jeśli
          partner przestanie działać z Tobą.
        </p>
      </div>

      {/* Endpoint info */}
      <div className="rounded-xl border border-[#1f3a8a]/20 bg-[#1f3a8a]/5 p-4 space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-[#1f3a8a]">Publiczny endpoint dla partnerów</div>
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-white px-3 py-1.5 rounded border border-slate-200 flex-1 truncate">
            GET {publicUrl}
          </code>
          <button
            data-testid="copy-url"
            onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("URL skopiowany"); }}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#1f3a8a] hover:text-[#dc2626] px-2.5 py-1.5 rounded border border-slate-300 hover:border-[#dc2626] transition"
          >
            <Copy size={12} /> Kopiuj URL
          </button>
        </div>
        <div className="text-xs text-slate-600 mt-1">
          Autoryzacja: nagłówek <code className="bg-white px-1 rounded">X-API-Key: rmk_...</code>{" "}
          lub <code className="bg-white px-1 rounded">Authorization: Bearer rmk_...</code>
        </div>
      </div>

      {/* Nowy klucz */}
      <div className="rounded-xl border border-slate-200 p-4">
        <div className="text-sm font-bold text-slate-700 mb-2">Wygeneruj nowy klucz</div>
        <div className="flex gap-2">
          <input
            data-testid="new-key-name"
            type="text"
            className="rm-input flex-1"
            placeholder="Nazwa partnera (np. Widget na stronie X, Firma Kowalscy)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
          />
          <button
            data-testid="create-key-btn"
            onClick={create}
            disabled={creating || !newName.trim()}
            className="rm-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Plus size={16} /> {creating ? "Tworzę..." : "Wygeneruj"}
          </button>
        </div>
      </div>

      {/* Lista kluczy */}
      <div>
        <div className="text-sm font-bold text-slate-700 mb-3">
          Twoje klucze API ({keys.length})
        </div>
        {loading ? (
          <div className="text-slate-500 text-sm py-6 text-center">Wczytuję...</div>
        ) : keys.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center italic border border-dashed border-slate-200 rounded-lg">
            Brak kluczy. Wygeneruj pierwszy powyżej.
          </div>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div
                key={k.id}
                data-testid={`api-key-row-${k.id}`}
                className={`p-4 rounded-lg border ${k.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-bold text-slate-800 truncate">{k.name || "(bez nazwy)"}</div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        k.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
                      }`}>
                        {k.active ? "Aktywny" : "Zablokowany"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <code
                        className="text-xs font-mono bg-slate-100 px-2 py-1 rounded flex-1 min-w-0 truncate cursor-pointer hover:bg-slate-200"
                        onClick={() => setRevealed({ ...revealed, [k.id]: !revealed[k.id] })}
                        title={revealed[k.id] ? "Kliknij aby ukryć" : "Kliknij aby pokazać"}
                      >
                        {revealed[k.id] ? k.key : maskKey(k.key)}
                      </code>
                      <button
                        data-testid={`copy-${k.id}`}
                        onClick={() => copyKey(k)}
                        className="p-2 rounded border border-slate-300 hover:bg-slate-100"
                        title="Kopiuj klucz"
                      >
                        {copied === k.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                      </button>
                    </div>
                    <div className="flex gap-4 text-xs text-slate-500 mt-2 flex-wrap">
                      <span>Utworzony: {k.created_at ? new Date(k.created_at).toLocaleString("pl-PL") : "-"}</span>
                      <span>Ostatnio użyty: {k.last_used_at ? new Date(k.last_used_at).toLocaleString("pl-PL") : "nigdy"}</span>
                      <span>Użyć: <b>{k.request_count || 0}</b></span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`docs-html-${k.id}`}
                      onClick={() => downloadDocs(k, "html")}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#1f3a8a] hover:text-white hover:bg-[#1f3a8a] px-3 py-1.5 rounded border border-[#1f3a8a]/30 hover:border-[#1f3a8a] transition"
                      title="Pobierz instrukcję dla partnera (HTML)"
                    >
                      <FileText size={13} /> HTML
                    </button>
                    <button
                      data-testid={`docs-pdf-${k.id}`}
                      onClick={() => downloadDocs(k, "pdf")}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#dc2626] hover:text-white hover:bg-[#dc2626] px-3 py-1.5 rounded border border-[#dc2626]/30 hover:border-[#dc2626] transition"
                      title="Pobierz instrukcję dla partnera (PDF)"
                    >
                      <FileDown size={13} /> PDF
                    </button>
                    <button
                      data-testid={`toggle-${k.id}`}
                      onClick={() => toggleActive(k)}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded border transition ${
                        k.active
                          ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                          : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      }`}
                    >
                      {k.active ? <><PowerOff size={13} /> Zablokuj</> : <><Power size={13} /> Aktywuj</>}
                    </button>
                    <button
                      data-testid={`delete-${k.id}`}
                      onClick={() => remove(k)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-white hover:bg-red-500 px-3 py-1.5 rounded border border-red-200 hover:border-red-500 transition"
                    >
                      <Trash2 size={13} /> Usuń
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Wskazówki */}
      <div className="text-xs text-slate-500 border-t border-slate-200 pt-4 leading-relaxed">
        💡 <b>Wskazówki bezpieczeństwa:</b> Każdy partner powinien mieć osobny klucz (żeby przy problemie zablokować
        tylko jego). Nigdy nie umieszczaj klucza w publicznym kodzie JavaScript widocznym w źródle strony —
        klucz powinien być używany <b>po stronie serwera partnera</b> (backend proxy) albo w środowisku
        gdzie nikt inny go nie zobaczy.
      </div>
    </div>
  );
}
